import React, { useState, useContext } from "react";
import { useDropzone } from "react-dropzone";
import { DataContext } from "../DataContexts/DataContext";
import { PlaylistDataContext } from "../DataContexts/PlaylistDataContext";
import { UploadContext } from "../DataContexts/UploadContext";
import "./TrackUpload.css";

const TrackUpload = () => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectAll, setSelectAll] = useState({
    neverIdle: false,
    onDemand: false,
    neverNew: false,
    mix: false,
  });
  const { loading, error, artists, createArtist, createAlbum, addSongToAlbum, exportAllGenres, tags: allTags } = useContext(DataContext);
  const { playlists, updatePlaylist } = useContext(PlaylistDataContext);
  const { uploadTrack, addNotification, AddCoverArtFromTrack } = useContext(UploadContext);
  const [isUploading, setIsUploading] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const [uploadResults, setUploadResults] = useState([]);
  const [selectAllGenre, setSelectAllGenre] = useState("");
  const [selectAllYear, setSelectAllYear] = useState("");
  const [headerTagInput, setHeaderTagInput] = useState('');
  const [headerPlaylistInput, setHeaderPlaylistInput] = useState('');


  const onDrop = (acceptedFiles) => {
    const audioFiles = acceptedFiles.filter(
      (file) => file.name.endsWith(".mp3") || file.name.endsWith(".m4a")
    );

    if (audioFiles.length === 0) {
      alert("Only MP3 or M4A files are allowed!");
      return;
    }

    audioFiles.forEach((file) => {
      window.jsmediatags.read(file, {
        onSuccess: (tag) => {
          const artist = tag.tags.artist || "Unknown";
          const title = tag.tags.title || "Unknown";
          const releaseYear = parseInt(tag.tags.year) || "Unknown";
          const album = tag.tags.album || "Unknown";
          const genre = tag.tags.genre || "";



          let pictureData = "";
          if (tag.tags.picture) {
            const { data, format } = tag.tags.picture;
            let base64String = "";
            for (let i = 0; i < data.length; i++) {
              base64String += String.fromCharCode(data[i]);
            }
            pictureData = `data:${format};base64,${btoa(base64String)}`;
          }

          setUploadedFiles((prevFiles) => [
            ...prevFiles,
            {
              file, // Include the file object for uploading
              name: file.name,
              artist,
              title,
              album,
              releaseYear,
              tags: [],
              playlists: [],
              genre,
              neverIdle: false,
              onDemand: false,
              neverNew: false,
              mix: false,
              picture: pictureData, // Store album artwork
            },
          ]);
        },
        onError: (error) => {
          addNotification(`Failed to read metadata for file: ${file.name}`, "error");
        },
      });
    });
  };

  const handleUpload = async () => {
    if (uploadedFiles.length === 0) return;

    setIsUploading(true);
    setSuccessCount(0);
    setUploadResults([]);
    let currentArtists = [...artists];
    let tempFiles = [...uploadedFiles];
    const successfulUploads = [];
    // Per-file outcome list rendered below the table (DJB-98); updated as
    // each file finishes so results show while the run is still going
    const results = [];
    const reportResult = (status, fileEntry, trackId) => {
      results.push({
        status,
        title: fileEntry.title,
        artist: fileEntry.artist,
        trackId: trackId || null,
      });
      setUploadResults([...results]);
    };
    for (let i = 0; i < tempFiles.length; i++) {
      let fileEntry = tempFiles[i];
      let artistId, albumId;

      // Check if the artist exists
      const matchedArtist = currentArtists.find(
        (artist) => artist.name.toLowerCase() === fileEntry.artist.toLowerCase()
      );

      if (matchedArtist) {
        artistId = matchedArtist.id;
      } else {
        try {
          // Create the artist and get their ID
          const newArtist = await createArtist(fileEntry.artist);
          artistId = newArtist.id;
          currentArtists = [...currentArtists, newArtist];
        } catch (error) {
          reportResult("failed", fileEntry);
          continue; // Skip this file
        }
      }

      // Check if the album exists for the artist
      const matchedAlbum = currentArtists
        .find((artist) => artist.id === artistId)
        ?.albums.find((album) => album.name.toLowerCase() === fileEntry.album.toLowerCase());

      if (matchedAlbum) {
        albumId = matchedAlbum.id;
      } else {
        try {
          // Create the album and get its ID
          const newAlbum = await createAlbum(artistId, fileEntry.album);

          currentArtists = currentArtists.map(artist =>
            artist.id === artistId
              ? { ...artist, albums: [...artist.albums, newAlbum] }
              : artist);
          albumId = newAlbum.id;

          if (albumId && fileEntry.picture) {
            await AddCoverArtFromTrack(albumId, fileEntry.picture);
          }
        } catch (error) {
          reportResult("failed", fileEntry);
          continue; // Skip this file
        }
      }

      // Create the track metadata object
      const trackMetadata = {
        title: fileEntry.title,
        releaseyear: fileEntry.releaseYear,
        tags: fileEntry.tags,
        neverIdle: fileEntry.neverIdle,
        ondemand: fileEntry.onDemand,
        neverNew: fileEntry.neverNew,
        mix: fileEntry.mix,
        trackLanguage: "",
        genre: fileEntry.genre,
        subgenre: "",
        albumId: albumId
      };

      // Convert the trackMetadata JSON object to a binary format (Blob)
      const trackMetadataBlob = new Blob([JSON.stringify(trackMetadata)], { type: "application/json" });

      // Prepare the form data for upload
      const formData = new FormData();
      formData.append("file", fileEntry.file); // Attach the file object
      formData.append("trackDTO", trackMetadataBlob); // Append track metadata as a binary blob

      try {
        const track = await uploadTrack(albumId, formData);

        addSongToAlbum(artistId, albumId, track);
        tempFiles.splice(i, 1);
        i--;
        setSuccessCount((prevCount) => prevCount + 1);
        setUploadedFiles([...tempFiles]);
        reportResult("success", fileEntry, track?.id);

        // Upload cover art if it exists
        if (track?.id && fileEntry.picture) {
          await AddCoverArtFromTrack(track.id, fileEntry.picture);
        }
        
        // Add track to selected playlists
        if (track?.id && fileEntry.playlists && fileEntry.playlists.length > 0) {
          successfulUploads.push({
            trackId: track.id,
            playlistIds: fileEntry.playlists
          });
        }
      } catch (error) {
        reportResult("failed", fileEntry);
      }
    }
    if (successfulUploads.length > 0) {
      await addTracksToPlaylistsBatch(successfulUploads);
    }
    setIsUploading(false);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "audio/*": [".mp3", ".m4a"],
    },
  });

  const handleTagChangeForAll = (tagName) => {
    const tag = allTags.find((t) => t.name === tagName);
    if (tag) {
      setUploadedFiles((prevFiles) =>
        prevFiles.map((file) =>
          file.tags.includes(tag.id) ? file : { ...file, tags: [...file.tags, tag.id] }
        )
      );
    }
  };

  const handlePlaylistChangeForAll = (playlistName) => {
    const playlist = playlists.find((p) => p.name.toLowerCase() === playlistName.toLowerCase() && p.playlistType === 'CUSTOM_GLOBAL');
    if (playlist) {
      setUploadedFiles((prevFiles) =>
        prevFiles.map((file) =>
          file.playlists.includes(playlist.id) ? file : { ...file, playlists: [...file.playlists, playlist.id] }
        )
      );
    }
  };

 // New batch method to handle all playlist updates
const addTracksToPlaylistsBatch = async (successfulUploads) => {
  // Group by playlist to avoid multiple updates to the same playlist
  const playlistUpdates = new Map();
  
  // Build the update map
  successfulUploads.forEach(({ trackId, playlistIds }) => {
    playlistIds.forEach(playlistId => {
      if (!playlistUpdates.has(playlistId)) {
        const playlist = playlists.find(p => p.id === playlistId);
        if (playlist) {
          playlistUpdates.set(playlistId, {
            playlist: { ...playlist },
            trackIds: []
          });
        }
      }
      playlistUpdates.get(playlistId).trackIds.push(trackId);
    });
  });
  
  // Update each playlist with all its new tracks at once
  for (const [playlistId, updateData] of playlistUpdates) {
    try {
      const updatedPlaylist = {
        ...updateData.playlist,
        tracks: [...updateData.playlist.tracks, ...updateData.trackIds]
      };
      await updatePlaylist(updatedPlaylist);
    } catch (error) {
      console.error(`Failed to update playlist ${playlistId}:`, error);
    }
  }
};

  const handleSetAllReleaseYears = () => {
    const year = parseInt(selectAllYear, 10);
    const currentYear = new Date().getFullYear();

    if (year >= 1900 && year <= currentYear) {
      setUploadedFiles((prevFiles) =>
        prevFiles.map((file) => ({ ...file, releaseYear: year }))
      );
    }
  };


  const handleSetAllGenres = () => {
    if (selectAllGenre.trim() !== "") {
      setUploadedFiles((prevFiles) =>
        prevFiles.map((file) => ({ ...file, genre: selectAllGenre }))
      );
    }
  };


  const handleSelectAllChange = (property) => {
    const newValue = !selectAll[property];
    setSelectAll((prev) => ({ ...prev, [property]: newValue }));
    
    // If mix is being checked, also check the other 3 properties
    if (property === 'mix' && newValue) {
      setSelectAll((prev) => ({ 
        ...prev, 
        mix: true,
        neverIdle: true,
        onDemand: true,
        neverNew: true
      }));
      setUploadedFiles((prevFiles) =>
        prevFiles.map((file) => ({ 
          ...file, 
          mix: true,
          neverIdle: true,
          onDemand: true,
          neverNew: true
        }))
      );
    } else if (property === 'neverIdle' || property === 'onDemand' || property === 'neverNew') {
      // If any of the 3 checkboxes is unchecked, uncheck mix
      if (!newValue) {
        setSelectAll((prev) => ({ ...prev, [property]: false, mix: false }));
        setUploadedFiles((prevFiles) =>
          prevFiles.map((file) => ({ ...file, [property]: false, mix: false }))
        );
      } else {
        setUploadedFiles((prevFiles) =>
          prevFiles.map((file) => ({ ...file, [property]: true }))
        );
      }
    } else {
      setUploadedFiles((prevFiles) =>
        prevFiles.map((file) => ({ ...file, [property]: newValue }))
      );
    }
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleRemoveAllFiles = () => {
    setUploadedFiles([]);
  };

  if (loading) {
    return (
      <div>
        <h1>Upload</h1>
        <p>Loading data, please wait... 🎵</p>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h1>Upload</h1>
        <p>Error loading data: {error}</p>
      </div>
    );
  }

  return (
    // The section-upload anchor and the collapsible shell live on the
    // DashboardSection wrapper in LibraryHeader (DJB-159)
    <div className="track-upload-wrapper">
      {/* Dropzone */}
      <div
        {...getRootProps()}
        className="track-upload-container small-banner"
      >
        <input {...getInputProps()} />
        <div className={`drop-area ${isDragActive ? "active" : ""}`}>
          {isDragActive ? (
            <p>Drop the files here...</p>
          ) : (
            <p>Drag one or more MP3 or M4A files to upload</p>
          )}
        </div>
      </div>

      {/* Toolbar: status, remove-all and upload */}
      <div className="upload-button-container">
        <span className="upload-status">
          {successCount}/{uploadedFiles.length + successCount}
        </span>
        {uploadedFiles.length > 0 && (
          <button
            className="remove-all-button"
            onClick={handleRemoveAllFiles}
            disabled={isUploading}
          >
            Remove all
          </button>
        )}
        <button className="upload-button" onClick={handleUpload} disabled={isUploading || addSongToAlbum === 0}>
          {isUploading ? "Uploading..." : "Upload Files"}
        </button>
      </div>

      {/* Table of uploaded files */}
      {uploadedFiles.length > 0 && (
        <div className="uploaded-files-table-wrapper">
        <table className="uploaded-files-table">
          <thead>
            <tr>
              {/* DJB-97: no File Name column; fixed widths keep the filled
                  table inside the narrower max-width */}
              <th className="col-cover">Cover</th>
              <th className="col-artist">Artist</th>
              <th className="col-title">Title</th>
              <th className="col-album">Album</th>
              <th className="col-year">
                Release Year
                <div>
                  <input
                    type="number"
                    placeholder="All"
                    title="Set year for all"
                    value={selectAllYear}
                    min="1900"
                    max={new Date().getFullYear()} // Dynamic max year
                    onChange={(e) => setSelectAllYear(e.target.value)}
                    onBlur={() => handleSetAllReleaseYears()} // Apply when losing focus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSetAllReleaseYears(); // Apply when pressing Enter
                    }}
                  />
                </div>
              </th>
              <th className="col-genre">
                Genre
                <div>
                  <input
                    list="genre-suggestions"
                    placeholder="Set for all"
                    title="Set genre for all"
                    value={selectAllGenre}
                    onChange={(e) => setSelectAllGenre(e.target.value)} // Update input live
                    onBlur={() => handleSetAllGenres()} // Apply when input loses focus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSetAllGenres(); // Apply when pressing Enter
                    }}
                  />
                  <datalist id="genre-suggestions">
                    {exportAllGenres().map((genre, index) => (
                      <option key={index} value={genre} />
                    ))}
                  </datalist>
                </div>
              </th>
              <th className="col-playlists">
                Playlists
                <div>
                  <input
                    list="playlist-suggestions"
                    placeholder="Add to all"
                    title="Add playlist to all"
                    value={headerPlaylistInput}
                    onChange={(e) => setHeaderPlaylistInput(e.target.value)} // Update input live
                    onBlur={() => handlePlaylistChangeForAll(headerPlaylistInput)} // Apply when input loses focus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handlePlaylistChangeForAll(headerPlaylistInput); // Apply when pressing Enter
                    }}
                  />
                  <datalist id="playlist-suggestions">
                    {playlists
                      .filter(playlist => playlist.playlistType === 'CUSTOM_GLOBAL')
                      .map((playlist) => (
                        <option key={playlist.id} value={playlist.name} />
                      ))}
                  </datalist>
                </div>
              </th>

              <th className="col-tags">
                Tags
                <div>
                  <input
                    list="all-tags-suggestions"
                    placeholder="Add to all"
                    title="Add tag to all"
                    value={headerTagInput}
                    onChange={(e) => setHeaderTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleTagChangeForAll(headerTagInput);
                        setHeaderTagInput('');  // Clear only after adding
                      }
                    }}
                  />
                  <datalist id="all-tags-suggestions">
                    {allTags
                      .filter(tag => tag.name.toLowerCase().includes(headerTagInput.toLowerCase()))
                      .map((tag) => (
                        <option key={tag.id} value={tag.name} />
                      ))}
                  </datalist>
                </div>
              </th>
              <th className="col-flag">
                Never Idle
                <div>
                  <input
                    type="checkbox"
                    checked={selectAll.neverIdle}
                    onChange={() => handleSelectAllChange("neverIdle")}
                  />
                </div>
              </th>
              <th className="col-flag">
                On Demand
                <div>
                  <input
                    type="checkbox"
                    checked={selectAll.onDemand}
                    onChange={() => handleSelectAllChange("onDemand")}
                  />
                </div>
              </th>
              <th className="col-flag">
                Never New
                <div>
                  <input
                    type="checkbox"
                    checked={selectAll.neverNew}
                    onChange={() => handleSelectAllChange("neverNew")}
                  />
                </div>
              </th>
              <th className="col-flag">
                Mix
                <div>
                  <input
                    type="checkbox"
                    checked={selectAll.mix}
                    onChange={() => handleSelectAllChange("mix")}
                  />
                </div>
              </th>
              <th className="col-remove" aria-label="Remove"></th>
            </tr>
          </thead>
          <tbody>
            {uploadedFiles.map((file, index) => (
              <tr key={index}>
                <td className="col-cover">
                  {file.picture ? (
                    <img src={file.picture} alt="Album Art" className="cover-thumb" />
                  ) : (
                    <span className="cover-placeholder" title={file.name}>♪</span>
                  )}
                </td>
                <td className="col-artist" title={file.artist}>{file.artist}</td>
                <td className="col-title">
                  <input
                    type="text"
                    value={file.title}
                    onChange={(e) =>
                      setUploadedFiles((prevFiles) =>
                        prevFiles.map((f, i) =>
                          i === index ? { ...f, title: e.target.value } : f
                        )
                      )
                    }
                  />
                </td>
                <td className="col-album" title={file.album}>{file.album}</td>
                <td className="col-year">
                  <input
                    type="number"
                    value={file.releaseYear}
                    min="1900"
                    max={new Date().getFullYear()}
                    onChange={(e) => {
                      const newYear = e.target.value;
                      if (newYear >= 1900 && newYear <= new Date().getFullYear()) {
                        setUploadedFiles((prevFiles) =>
                          prevFiles.map((f, i) =>
                            i === index ? { ...f, releaseYear: newYear } : f
                          )
                        );
                      }
                    }}
                  />
                </td>
                <td className="col-genre">
                  <input
                    list={`genre-suggestions-${index}`}
                    type="text"
                    value={file.genre}
                    onChange={(e) =>
                      setUploadedFiles((prevFiles) =>
                        prevFiles.map((f, i) =>
                          i === index ? { ...f, genre: e.target.value } : f
                        )
                      )
                    }
                  />
                  <datalist id={`genre-suggestions-${index}`}>
                    {exportAllGenres().map((genre, i) => (
                      <option key={i} value={genre} />
                    ))}
                  </datalist>
                </td>
                <td className="col-playlists">
                  {/* DJB-99: selected playlists are removable chips, same
                      behavior as the Tags column */}
                  <div className="tag-containerSong">
                    {file.playlists.map((playlistId) => {
                      const playlist = playlists.find((p) => p.id === playlistId);
                      return (
                        <span key={playlistId} className="tag-chipSong">
                          {playlist ? playlist.name : playlistId}
                          <button
                            className="remove-tag-buttonSong"
                            title="Remove playlist"
                            onClick={() => {
                              setUploadedFiles((prevFiles) =>
                                prevFiles.map((f, i) =>
                                  i === index
                                    ? { ...f, playlists: f.playlists.filter((id) => id !== playlistId) }
                                    : f
                                )
                              );
                            }}
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                  <input
                    list={`playlist-suggestions-${index}`}
                    placeholder="Add playlist"
                    onChange={(e) => {
                      const playlist = playlists.find((p) => p.name === e.target.value && p.playlistType === 'CUSTOM_GLOBAL');
                      if (playlist) {
                        setUploadedFiles((prevFiles) =>
                          prevFiles.map((f, i) =>
                            i === index && !f.playlists.includes(playlist.id)
                              ? { ...f, playlists: [...f.playlists, playlist.id] }
                              : f
                          )
                        );
                        e.target.value = "";
                      }
                    }}
                  />
                  <datalist id={`playlist-suggestions-${index}`}>
                    {playlists
                      .filter(playlist => playlist.playlistType === 'CUSTOM_GLOBAL')
                      .map((playlist) => (
                        <option key={playlist.id} value={playlist.name} />
                      ))}
                  </datalist>
                </td>
                <td className="col-tags">
                  <div className="tag-containerSong">
                    {file.tags.map((tagId) => {
                      const tag = allTags.find(t => t.id === tagId);
                      return (
                        <span key={tagId} className="tag-chipSong">
                          {tag ? tag.name : tagId}
                          <button 
                            className="remove-tag-buttonSong" 
                            onClick={() => {
                              setUploadedFiles((prevFiles) =>
                                prevFiles.map((f, i) =>
                                  i === index ? { ...f, tags: f.tags.filter(id => id !== tagId) } : f
                                )
                              );
                            }}
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                  <input
                    list={`tags-suggestions-${index}`}
                    placeholder="Add tag"
                    onChange={(e) => {
                      const tag = allTags.find((t) => t.name === e.target.value);
                      if (tag) {
                        setUploadedFiles((prevFiles) =>
                          prevFiles.map((f, i) =>
                            i === index ? { ...f, tags: [...f.tags, tag.id] } : f
                          )
                        );
                        e.target.value = "";
                      }
                    }}
                  />
                  <datalist id={`tags-suggestions-${index}`}>
                    {allTags.map((tag) => (
                      <option key={tag.id} value={tag.name} />
                    ))}
                  </datalist>
                </td>
                <td className="col-flag">
                  <input
                    type="checkbox"
                    checked={file.neverIdle}
                    onChange={() => {
                      const newValue = !file.neverIdle;
                      setUploadedFiles((prevFiles) =>
                        prevFiles.map((f, i) => {
                          if (i === index) {
                            // If unchecking, also uncheck mix
                            return { ...f, neverIdle: newValue, mix: newValue ? f.mix : false };
                          }
                          return f;
                        })
                      );
                    }}
                  />
                </td>
                <td className="col-flag">
                  <input
                    type="checkbox"
                    checked={file.onDemand}
                    onChange={() => {
                      const newValue = !file.onDemand;
                      setUploadedFiles((prevFiles) =>
                        prevFiles.map((f, i) => {
                          if (i === index) {
                            // If unchecking, also uncheck mix
                            return { ...f, onDemand: newValue, mix: newValue ? f.mix : false };
                          }
                          return f;
                        })
                      );
                    }}
                  />
                </td>
                <td className="col-flag">
                  <input
                    type="checkbox"
                    checked={file.neverNew}
                    onChange={() => {
                      const newValue = !file.neverNew;
                      setUploadedFiles((prevFiles) =>
                        prevFiles.map((f, i) => {
                          if (i === index) {
                            // If unchecking, also uncheck mix
                            return { ...f, neverNew: newValue, mix: newValue ? f.mix : false };
                          }
                          return f;
                        })
                      );
                    }}
                  />
                </td>
                <td className="col-flag">
                  <input
                    type="checkbox"
                    checked={file.mix}
                    onChange={() => {
                      const newValue = !file.mix;
                      setUploadedFiles((prevFiles) =>
                        prevFiles.map((f, i) => {
                          if (i === index) {
                            // When Mix is checked, also check the other 3
                            if (newValue) {
                              return { 
                                ...f, 
                                mix: true,
                                neverIdle: true,
                                onDemand: true,
                                neverNew: true
                              };
                            } else {
                              return { ...f, mix: false };
                            }
                          }
                          return f;
                        })
                      );
                    }}
                  />
                </td>
                <td className="col-remove">
                  {/* DJB-97: red minus, same control language as the other
                      admin tables (Curator ps-icon-btn--remove) */}
                  <button
                    className="remove-file-button"
                    title="Remove from upload list"
                    aria-label="Remove from upload list"
                    onClick={() => handleRemoveFile(index)}
                  >
                    −
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {/* Upload results below the table (DJB-98). Success entries deep-link
          to the track in Browse (?tab=browse&songId=); they open in a new
          tab so following one mid-run never interrupts the uploads still
          going in this one. */}
      {uploadResults.length > 0 && (
        <div className="upload-results">
          <h3 className="upload-results-title">Upload results</h3>
          <ul className="upload-results-list">
            {uploadResults.map((result, index) => (
              <li
                key={index}
                className={`upload-result upload-result--${result.status}`}
              >
                <span className="upload-result-label">
                  {result.status === "success" ? "Success:" : "Failed:"}
                </span>{" "}
                {result.status === "success" && result.trackId ? (
                  <a
                    className="upload-result-link"
                    href={`/musicmanager?tab=browse&songId=${result.trackId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open this track in Browse"
                  >
                    {result.title} – {result.artist}
                  </a>
                ) : (
                  <span>
                    {result.title} – {result.artist}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default TrackUpload;